/**
 * UploadSwipesScreen.jsx
 * ----------------------
 * Screen for uploading biometric card swipe records via Excel, CSV, or text device logs.
 * Zero mock data, fully connected to backend /attendance/upload-swipes.
 */
import React from 'react'
import AttendanceSubNav from '../components/AttendanceSubNav'
import AppExcelUploadView from '../../../components/AppExcelUploadView'
import api from '../../../api/client'
import { appToast } from '../../../components/AppToast'

export default function UploadSwipesScreen() {
  const sampleColumns = ['Card / GR No', 'Date & Time (YYYY-MM-DD HH:MM:SS)', 'Device ID', 'Remark']

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/attendance/template', {
        responseType: 'blob',
      })
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'Biometric_Swipes_Template.xlsx'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      appToast.success('Template downloaded successfully.')
    } catch (err) {
      console.error('Failed to download swipes template', err)
      appToast.error('Failed to download template.')
    }
  }

  const handleUpload = async (file) => {
    const formData = new FormData()
    formData.append('file', file)

    try {
      const { data } = await api.post('/attendance/upload-swipes', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (data?.success) {
        appToast.success(data.message)
        return { success: true, count: data.imported_count }
      }
      throw new Error(data?.detail || 'Upload failed')
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Error uploading file'
      appToast.error(msg)
      return { success: false, error: msg }
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Biometric &amp; Swipes</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Upload Biometric Swipes
          </h1>
          <p className="mt-0.5 text-xs text-on-surface-variant">
            Import Excel, CSV, or text device swipe dump files to record biometric student attendance.
          </p>
        </div>
        <AttendanceSubNav />
      </section>

      <AppExcelUploadView
        title="Upload Machine Swipes (Excel/CSV/Text)"
        subtitle="Import attendance biometric punch records directly into the system"
        sampleFileName="Biometric_Swipes_Template.xlsx"
        onDownloadSample={handleDownloadTemplate}
        expectedColumns={sampleColumns}
        onUpload={handleUpload}
        cancelPath="/attendance/biometric"
        backLabel="View Swipes Log"
      />
    </div>
  )
}
